import Alert from "@material-ui/lab/Alert";
import axios from "axios";
import useElement from "backend/hooks/elements/useElement";
import useTopElements from "backend/hooks/model/useTopElements";
import useMyTasks from "backend/hooks/workflows/useMyTasks";
import useWorkflowLogs from "backend/hooks/workflows/useWorkflowLogs";
import { ICardElementWithChildren } from "backend/model/model";
import ElementLink from "components/link/ElementLink";
import WorkflowToolbar from "components/toolbars/WorkflowToolbar";
import FormButton from "design/components/buttons/FormButton";
import PeriodChip from "design/components/chips/PeriodChip";
import MeasureTable from "design/components/tables/MeasureTable";
import DashboardTitle from "design/components/texts/DashboardTitle";
import PageContainer from "design/layouts/PageContainer";
import SplitView from "design/layouts/SplitView";
import StackView from "design/layouts/StackView";
import Page from "design/pages/Page";
import { t } from "i18next";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";
import * as conf from "qpr-base/configuration";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import AlertMessage from "qpr-base/ui/components/AlertMessage";
import React from "react";
import { ReactComponent as CenterDotIcon } from "assets/CenterDotIcon.svg";

const CompletedTasks = () => {
  const [selectedWf, setSelectedWf] = React.useState<
    ICardElementWithChildren | undefined
  >(undefined);
  const [canReactivate, setCanreactivate] = React.useState(false);
  const [reActivationMessage, setReActivationMessage] = React.useState("");
  const { data: logs, isLoading: isLogsLoading } = useWorkflowLogs(
    selectedWf?.workflowState?.wfInstanceId || ""
  );
  const { data: instanceElement } = useElement(
    selectedWf?.workflowState?.measureId,
    selectedWf?.workflowState?.period
  );
  const reActivationAllowedwithConfig =
    !!conf.getCompletedWorkflowsViewSettings().allowReactivation;

  const ctx = useAppContext();

  const { data: myTasks, isLoading: isMyTasksLoading } =
    useMyTasks("completed");

  const reactivateWorkflow = async (
    wfId: string,
    check: boolean
  ): Promise<any> => {
    if (!reActivationAllowedwithConfig) return {};

    const queryParams = {
      wfId,
      check,
    };
    let response;
    try {
      response = await axios.post("workflow/instances/copy", queryParams);
    } catch (e) {
      return {};
    }
    return response?.data?.data || {};
  };

  const reActivate = async () => {
    if (!reActivationAllowedwithConfig) return;

    const wfId = selectedWf?.workflowState?.wfInstanceId;
    if (wfId) {
      const response = await reactivateWorkflow(wfId, false);
      if (response.wfId)
        setReActivationMessage(t("completedtasks.Workflow was reactivated"));
    }
  };

  const selectRow = React.useCallback(
    async (item: ICardElementWithChildren) => {
      if (item.workflowState?.wfInstanceId) {
        const response = await reactivateWorkflow(
          item.workflowState.wfInstanceId,
          true
        );
        setCanreactivate(
          !!response.canReactivate && reActivationAllowedwithConfig
        );
      } else setCanreactivate(false);
      if (reActivationAllowedwithConfig) setReActivationMessage("");
      setSelectedWf(item);
    },
    []
  );
  const { data: topElements, isLoading: topElementsLoading } = useTopElements();

  React.useEffect(() => {
    if (!selectedWf && myTasks?.length) {
      (async () => {
        const response = await reactivateWorkflow(
          myTasks[0].workflowState?.wfInstanceId || "",
          true
        );
        setCanreactivate(
          !!response.canReactivate && reActivationAllowedwithConfig
        );
        setReActivationMessage("");
        setSelectedWf(myTasks[0]);
      })();
    }
  }, [myTasks, isLogsLoading]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString();
  };
  if (!topElements || topElementsLoading) return null;

  const getElementType = (element: ICardElementWithChildren): string => {
    return element.typename || element.workflowState?.measureType || 'undefined';
  };

  const getState = (state: string) => {
    if (state === 'step-2') return 'approval';
    if (state === 'step-3') return 'finalapproval';
    if (state === 'reject-1') return 'approvalrejected';
    if (state === 'reject-2') return 'finalapprovalrejected';
    return state;
  }

  return (
    <Page isLoading={isMyTasksLoading || !myTasks} Toolbar={WorkflowToolbar}>
      <PageContainer width="100%">
        <SplitView>
          <MeasureTable
            keyFn={(item) => item?.workflowState?.wfInstanceId || ""}
            items={myTasks || []}
            selectedRow={selectedWf}
            columns={[
              {
                title: t("completedtasks.Type").toString(),
                value: (row) => {
                  return (
                  
                  <div>
                    <AlertMessage className="completedTask" severity="success">
                      {t("configtypes." + getElementType(row.elem)) || "undefined"}
                      <span style={{position: 'relative',top:'-2px'}}>
                      <CenterDotIcon />
                      </span>
                      {t(row.elem?.workflowState?.wfShortType || 'undefined')}
                    </AlertMessage>
                  </div>
                )},
                onRowClick: selectRow,
              },
              {
                title: t("workflowinput.table.Name").toString(),
                sortFn: (a, b) => a.name.localeCompare(b.name),
                onRowClick: selectRow,
                value: (item) => {
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        flexDirection: "column",
                      }}
                    >
                      <div>
                        {getTranslatedAttribute(
                          { JS_ATTR: item.elem?.workflowState?.metadata },
                          "Name",
                          ctx.language
                        )}
                      </div>
                      <div>
                        <i>
                        {getTranslatedAttribute(
                            topElements.find(
                              (topElement) =>
                                topElement.name === item.elem?.workflowState?.entity
                            ),
                            "Name",
                            ctx.language
                          )}
                        </i>
                      </div>
                    </div>
                  );
                },
              },
              {
                title: t("completedtasks.ACT").toString(),
                value: (row) => <div>{row.elem.ACT_prettyvalue}</div>,
                onRowClick: selectRow,
              },
              {
                title: t("completedtasks.Period").toString(),
                onRowClick: selectRow,
                value: (row) => <div>{row.elem.workflowState?.period}</div>,
              },
              {
                title: t("completedtasks.elementtype").toString(),
                onRowClick: selectRow,
                value: (row) => (
                  <div>{t(`configtypes.${getElementType(row.elem)}`)}</div>
                ),
              },
              {
                title: t("completedtasks.Date").toString(),
                onRowClick: selectRow,
                sortFn: (a, b) => a.name.localeCompare(b.name),
                value: (row) => (
                  <div>
                    {formatDate(row.elem.workflowState?.startdate || "")}
                  </div>
                ),
              },
            ]}
          />
          <StackView padding="10px">
            {selectedWf && logs ? (
              <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    marginLeft: "15px"
                  }}
                >
                  <DashboardTitle>
                    {getTranslatedAttribute(
                      { JS_ATTR: selectedWf.workflowState?.metadata },
                      "Name",
                      ctx.language
                    )}
                    {selectedWf.workflowState?.wfShortType !== 'delete' ? <ElementLink item={instanceElement} /> : <div style={{height:"39px"}}></div>}
                  </DashboardTitle>
                  {selectedWf.workflowState?.period !== '' ?
                  <div style={{ width: "140px" }}>
                    <PeriodChip
                      period={selectedWf?.workflowState?.period || ""}
                      icon="KPI"
                    />
                  </div> : null}
                </div>
                {reActivationMessage ? (
                  <Alert severity="success">{reActivationMessage}</Alert>
                ) : null}
                <MeasureTable
                  items={logs || []}
                  columns={[
                    {
                      title: t("completedtasks.User").toString(),
                      value: (row) => (
                        <div>
                          {row.elem.prevstate === '' && selectedWf.workflowState?.wfShortType === 'value' ? '-' : row.elem.username}
                        </div>
                      ),
                    },
                    {
                      title: t("completedtasks.State").toString(),
                      value: (row) => (
                        <div>
                          {" "}
                          {row.elem.prevstate === ""
                            ? t("completedtasks.created")
                            : t(`completedtasks.${getState(row.elem.prevstate)}`)}
                        </div>
                      ),
                    },
                    {
                      title: t("completedtasks.Date").toString(),
                      value: (row) => (
                        <div>
                          {new Date(row.elem.timestamp).toLocaleString()}
                        </div>
                      ),
                    },
                    {
                      title: t("completedtasks.Message").toString(),
                      value: (row) => <div>{row.elem.message}</div>,
                    },
                  ]}
                />

                {canReactivate ? (
                  <>
                    <FormButton
                      style={"approve"}
                      type="button"
                      disabled={reActivationMessage !== ""}
                      onClick={() => reActivate()}
                    >
                      {t("completedtasks.Reactivate")}
                    </FormButton>
                  </>
                ) : null}
              </>
            ) : null}
          </StackView>
        </SplitView>
      </PageContainer>
    </Page>
  );
};

export default CompletedTasks;
